import type { Permissions, Capability } from '../types';

/**
 * Returns true if the user holds the given capability.
 * Single source of truth for capability checks in the console.
 */
export function can(
  permissions: Permissions | undefined | null,
  capability: Capability,
): boolean {
  if (!permissions) return false;
  return permissions.capabilities[capability] === true;
}

/**
 * Returns true if the user holds the given role string in any of their
 * role assignments. Use `can()` for capability-based checks instead.
 */
export function hasRoleAssignment(
  permissions: Permissions | undefined | null,
  role: string,
): boolean {
  if (!permissions) return false;
  return permissions.assignments.some((a) => a.role === role);
}

/**
 * Menu capability declarations — maps each menu item to the capability
 * required to see it. Used by Layout.tsx and tested in isolation.
 */
export const MENU_CAPABILITIES = {
  analytics: 'view_analytics' as Capability,
  courses: 'manage_assessments' as Capability,
  users: 'manage_users' as Capability,
  assessments: 'manage_assessments' as Capability,
  events: 'view_events' as Capability,
  instances: 'schedule_instances' as Capability,
  attempts: 'record_attempts' as Capability,
  score_tables: 'manage_scoring_tables' as Capability,
  disqualifications: 'manage_assessments' as Capability,
  student_groups: 'manage_users' as Capability,
  students: 'manage_users' as Capability,
  surveys: 'manage_assessments' as Capability,
  teams: 'manage_teams' as Capability,
  org_units: 'manage_org' as Capability,
  assignments: 'schedule_instances' as Capability,
  rankings: 'manage_org' as Capability,
  licensing: 'manage_licensing' as Capability,
  data_exchange: 'manage_integrations' as Capability,
} as const;

export type MenuItemKey = keyof typeof MENU_CAPABILITIES;

/** Returns true when at least one item in `keys` is accessible. */
export function canSeeSection(
  permissions: Permissions | undefined | null,
  keys: MenuItemKey[],
): boolean {
  return keys.some((k) => can(permissions, MENU_CAPABILITIES[k]));
}
